import { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Box, ChevronLeftOutline, ChevronRightOutline, IconButton, rem, useBreakpointValue } from '@bonusly/design-system';
import { useQueryControl } from '@bonusly/url';

import { useSideScroll } from '../../../../hooks/use-side-scroll';
import { Announcement } from '../../types/announcements';
import { AnnouncementCard } from '../announcement-card';
import { EmptyState } from './empty-state';

interface AnnouncementsCarouselContentProps {
	announcements: Announcement[];
	legacy?: boolean; // legacy carousel uses a different layout
	selectedAnnouncementId?: string;
}

const CARD_WIDTH = { base: rem(300), xs: rem(550) };

export const AnnouncementsCarouselContent: FC<AnnouncementsCarouselContentProps> = ({
	announcements,
	legacy = false,
	selectedAnnouncementId: propSelectedId,
}) => {
	const screenSize = useBreakpointValue({ base: 'mobile', xs: 'desktop' });
	const { canScrollLeft, canScrollRight, getButtonProps, getContainerProps } = useSideScroll({
		increment: screenSize === 'desktop' ? 540 : 300,
	});

	const { t } = useTranslation(['common', 'announcements']);
	const { track } = useAnalytics();
	const alreadyInteractedWithCarousel = useRef(false);
	const { query: urlSelectedId } = useQueryControl('announcement');

	// Use prop ID if provided, otherwise fall back to URL query parameter
	const selectedAnnouncementId = propSelectedId || urlSelectedId;

	useEffect(() => {
		if (selectedAnnouncementId) {
			const element = document.getElementById(`announcement-${selectedAnnouncementId}`);
			if (element) {
				element.scrollIntoView({ behavior: 'smooth' });
			}
		}
	}, [selectedAnnouncementId]);

	const trackInteraction = () => {
		// avoid tracking too many interactions
		if (alreadyInteractedWithCarousel.current) return;
		track(AnalyticsEvent.Announcements.AnnouncementCarouselInteraction);
		alreadyInteractedWithCarousel.current = true;
	};

	// When multiple announcements exist, show a peek of the next announcement by not using full width
	const onlyOneAnnouncement = announcements.length === 1;

	return (
		<Box position="relative" display="flex" alignItems="center" data-testid="announcements-carousel-wrapper">
			{canScrollLeft && (
				<>
					{!legacy && (
						<Box
							position="absolute"
							left="0"
							width="12%"
							height="100%"
							top="0"
							bg="linear-gradient(90deg, rgb(246 246 249) 0%, rgba(237, 238, 243, 0) 100%)"
							zIndex={1}
						/>
					)}
					<IconButton
						icon={<ChevronLeftOutline />}
						aria-label={t('common:go_back')}
						borderRadius="full"
						color="white"
						backgroundColor="black"
						_hover={{ backgroundColor: 'black' }}
						zIndex={99}
						position="absolute"
						top="50%"
						left={3}
						transform="translateY(-50%)"
						onClick={() => {
							getButtonProps('left').onClick();
							trackInteraction();
						}}
					/>
				</>
			)}
			<Box
				{...{ style: { scrollbarWidth: 'none' } }}
				flex={1}
				overflowX={onlyOneAnnouncement ? 'hidden' : 'scroll'}
				display="flex"
				gap={5}
				{...getContainerProps()}
			>
				<Box
					display="flex"
					p={legacy ? rem(16) : 0}
					gap={3}
					flexWrap="nowrap"
					width={onlyOneAnnouncement || !announcements.length ? '100%' : 'auto'}
					minH={!announcements.length ? '200px' : undefined}
					justifyContent={!announcements.length ? 'center' : undefined}
					alignItems={!announcements.length ? 'center' : undefined}
				>
					{announcements.length ? (
						announcements.map((announcement) => (
							<AnnouncementCard
								key={announcement.id}
								id={`announcement-${announcement.id}`}
								announcement={announcement}
								width={onlyOneAnnouncement ? '100%' : CARD_WIDTH}
							/>
						))
					) : (
						<EmptyState />
					)}
				</Box>
			</Box>
			{canScrollRight && (
				<>
					{!legacy && (
						<Box
							position="absolute"
							right="0"
							width="12%"
							height="100%"
							top="0"
							bg="linear-gradient(270deg, rgb(246 246 249) 0%, rgba(237, 238, 243, 0) 100%)"
							zIndex={1}
						/>
					)}
					<IconButton
						data-testid="right-button"
						icon={<ChevronRightOutline />}
						aria-label={t('common:next')}
						position="absolute"
						borderRadius="full"
						zIndex={99}
						color="white"
						backgroundColor="black"
						_hover={{ backgroundColor: 'black' }}
						top="50%"
						right={3}
						transform="translateY(-50%)"
						onClick={() => {
							getButtonProps('right').onClick();
							trackInteraction();
						}}
					/>
				</>
			)}
		</Box>
	);
};
